// src/components/dashboard/BookUserHistoryModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import {
  X,
  CheckCircle,
  BookOpen,
  Search,
  RotateCcw,
  Package,
  Layers,
  ExternalLink,
} from "lucide-react";
import config from "../../config";
import OrderManager from "../../services/dashboard/OrderManager";
import { Link } from "react-router-dom";

const STATUS_CONFIG = {
  PROCESSING: {
    label: "Processing",
    badge: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
    dot: "bg-gray-500",
  },
  SHIPPED: {
    label: "Shipped",
    badge: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800",
    dot: "bg-blue-500",
  },
  ON_THE_WAY: {
    label: "On the Way",
    badge: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-800",
    dot: "bg-indigo-500",
  },
  BOOK_WITH_ME: {
    label: "With User (Delivered)",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800",
    dot: "bg-emerald-500",
  },
  RETURN_REQUESTED: {
    label: "Return Requested",
    badge: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800",
    dot: "bg-amber-500",
  },
  PICKUP_SCHEDULED: {
    label: "Pickup Scheduled",
    badge: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800",
    dot: "bg-orange-500",
  },
  RETURN_IN_TRANSIT: {
    label: "Return in Transit",
    badge: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800",
    dot: "bg-purple-500",
  },
  RETURNED: {
    label: "Returned",
    badge: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-800",
    dot: "bg-teal-500",
  },
  CANCELLED: {
    label: "Cancelled",
    badge: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800",
    dot: "bg-red-500",
  },
};

export default function BookUserHistoryModal({
  isOpen,
  onClose,
  book,
  authorName = "",
  categoryName = "",
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("ALL"); // ALL | CURRENT | RETURNED | CANCELLED
  const [searchTerm, setSearchTerm] = useState("");

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch orders for this book whenever modal opens
  useEffect(() => {
    if (isOpen && book?.bookId) {
      fetchBookOrders(book.bookId);
    } else {
      setOrders([]);
      setError(null);
      setSearchTerm("");
      setActiveTab("ALL");
    }
  }, [isOpen, book?.bookId]);

  const fetchBookOrders = async (bookId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await OrderManager.getOrdersByBookId(bookId);
      const ordersList = Array.isArray(data)
        ? data
        : data?.content || [];
      setOrders(ordersList);
    } catch (err) {
      console.error("Failed to fetch book user history:", err);
      setError("Failed to load order history for this book.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";
    let date;
    if (Array.isArray(dateInput) && dateInput.length >= 3) {
      date = new Date(
        dateInput[0],
        dateInput[1] - 1,
        dateInput[2],
        dateInput[3] || 0,
        dateInput[4] || 0
      );
    } else if (typeof dateInput === "string") {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return "N/A";
    }
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Determine if an order is active/currently with a user
  const isCurrentHolder = (order) => {
    if (!order) return false;
    if (order.orderStatus === "CANCELLED") return false;
    const status = order.deliveryStatus;
    return (
      status === "BOOK_WITH_ME" ||
      status === "RETURN_REQUESTED" ||
      status === "PICKUP_SCHEDULED" ||
      status === "RETURN_IN_TRANSIT" ||
      status === "ON_THE_WAY" ||
      status === "SHIPPED" ||
      status === "PROCESSING"
    );
  };

  // Metric counts
  const metrics = useMemo(() => {
    const totalOrders = orders.length;
    const currentOrders = orders.filter(isCurrentHolder);
    const returnedOrders = orders.filter(
      (o) => o.deliveryStatus === "RETURNED" || o.returnStatus === "RETURNED"
    );
    const cancelledOrders = orders.filter(
      (o) => o.orderStatus === "CANCELLED" || o.deliveryStatus === "CANCELLED"
    );

    return {
      total: totalOrders,
      current: currentOrders.length,
      returned: returnedOrders.length,
      cancelled: cancelledOrders.length,
    };
  }, [orders]);

  // Filtered orders list
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Tab filter
      if (activeTab === "CURRENT" && !isCurrentHolder(order)) return false;
      if (
        activeTab === "RETURNED" &&
        order.deliveryStatus !== "RETURNED" &&
        order.returnStatus !== "RETURNED"
      )
        return false;
      if (
        activeTab === "CANCELLED" &&
        order.orderStatus !== "CANCELLED" &&
        order.deliveryStatus !== "CANCELLED"
      )
        return false;

      // Search filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const userName = `${order.user?.firstName || ""} ${
          order.user?.lastName || ""
        }`.toLowerCase();
        const email = (order.user?.emailId || "").toLowerCase();
        const mobile = (order.user?.mobileNumber || "").toLowerCase();
        const orderIdStr = String(order.orderId || "");

        return (
          userName.includes(query) ||
          email.includes(query) ||
          mobile.includes(query) ||
          orderIdStr.includes(query)
        );
      }

      return true;
    });
  }, [orders, activeTab, searchTerm]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
      >
        {/* ─── Modal Header ────────────────────────────────────────── */}
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-850">
          <div className="flex items-center gap-4 min-w-0">
            {/* Book Thumbnail */}
            <div className="w-12 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0">
              {book?.bookImages?.length > 0 ? (
                <img
                  src={`${config.url}${book.bookImages[0]}`}
                  alt={book.bookName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <BookOpen size={20} />
                </div>
              )}
            </div>

            {/* Book Title & Meta */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                  Book User History
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  ID: #{book?.bookId}
                </span>
                {book?.bookLanguage && (
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    {book.bookLanguage}
                  </span>
                )}
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate mt-1">
                {book?.bookName || "Book Details"}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                by{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {authorName || "Unknown Author"}
                </span>{" "}
                {categoryName && `• ${categoryName}`}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* ─── Modal Body ──────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* ─── Metric Cards ────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 rounded-xl p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Total Orders
                </span>
                <Package size={16} className="text-blue-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {metrics.total}
              </div>
              <span className="text-[11px] text-gray-400">All-time borrows</span>
            </div>

            <div
              className={`border rounded-xl p-3.5 transition-colors ${
                metrics.current > 0
                  ? "bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/60"
                  : "bg-gray-50 dark:bg-gray-800/60 border-gray-200/70 dark:border-gray-700/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Currently With Users
                </span>
                <span
                  className={`relative flex h-2.5 w-2.5 ${
                    metrics.current > 0 ? "inline-flex" : "hidden"
                  }`}
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              </div>
              <div
                className={`text-2xl font-bold mt-1 ${
                  metrics.current > 0
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-gray-900 dark:text-white"
                }`}
              >
                {metrics.current}
              </div>
              <span className="text-[11px] text-gray-400">
                {metrics.current > 0 ? "Active readers" : "In inventory"}
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 rounded-xl p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Returned
                </span>
                <CheckCircle size={16} className="text-teal-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {metrics.returned}
              </div>
              <span className="text-[11px] text-gray-400">
                Completed cycles
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 rounded-xl p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Total Copies
                </span>
                <Layers size={16} className="text-orange-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {book?.bookCopies ?? "N/A"}
              </div>
              <span className="text-[11px] text-gray-400">
                Stock: {book?.bookStockStatus?.replace(/_/g, " ") || "N/A"}
              </span>
            </div>
          </div>

          {/* ─── Complete Borrowing History Section ───────────────── */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <RotateCcw size={16} className="text-blue-600" />
                Complete Borrowing History
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {orders.length}
                </span>
              </h3>

              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search user, email, order #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-800 pb-2 overflow-x-auto text-xs">
              <button
                onClick={() => setActiveTab("ALL")}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  activeTab === "ALL"
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                All Orders ({orders.length})
              </button>
              <button
                onClick={() => setActiveTab("CURRENT")}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  activeTab === "CURRENT"
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Currently Active ({metrics.current})
              </button>
              <button
                onClick={() => setActiveTab("RETURNED")}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  activeTab === "RETURNED"
                    ? "bg-teal-600 text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Returned ({metrics.returned})
              </button>
              <button
                onClick={() => setActiveTab("CANCELLED")}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  activeTab === "CANCELLED"
                    ? "bg-red-600 text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Cancelled ({metrics.cancelled})
              </button>
            </div>

            {/* ─── Orders Table ───────────────────────────────────── */}
            {loading ? (
              <div className="py-12 text-center text-gray-500 animate-pulse">
                Loading history...
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl text-center text-sm text-red-600 dark:text-red-400">
                {error}
                <button
                  onClick={() => fetchBookOrders(book.bookId)}
                  className="ml-3 underline font-semibold"
                >
                  Retry
                </button>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="py-10 text-center bg-gray-50 dark:bg-gray-800/40 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                <BookOpen size={28} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {searchTerm
                    ? "No matching orders found"
                    : "No orders found for this book"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {searchTerm
                    ? "Try clearing the search input."
                    : "This book has not been ordered by any user yet."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 uppercase font-semibold">
                    <tr>
                      <th className="px-4 py-3 text-left">Order #</th>
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-left">Ordered On</th>
                      <th className="px-4 py-3 text-left">Return Date</th>
                      <th className="px-4 py-3 text-left">Delivery Status</th>
                      <th className="px-4 py-3 text-left">Collected By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800">
                    {filteredOrders.map((order, idx) => {
                      const statusConf =
                        STATUS_CONFIG[order.deliveryStatus] ||
                        STATUS_CONFIG.PROCESSING;
                      const isCurrent = isCurrentHolder(order);

                      return (
                        <tr
                          key={order.orderId || idx}
                          className={`transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-750 ${
                            isCurrent
                              ? "bg-emerald-50/30 dark:bg-emerald-950/10"
                              : ""
                          }`}
                        >
                          {/* Order ID */}
                          <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                            #{order.orderId}
                          </td>

                          {/* User Name */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-bold flex items-center justify-center text-[11px] flex-shrink-0">
                                {order.user?.firstName?.[0]?.toUpperCase() || "U"}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-gray-900 dark:text-white truncate">
                                  {order.user?.firstName} {order.user?.lastName}
                                </div>
                                {order.user?.userId && (
                                  <Link
                                    to={`/admin/users/${order.user.userId}`}
                                    target="_blank"
                                    className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-0.5"
                                  >
                                    View user <ExternalLink size={9} />
                                  </Link>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Contact Info */}
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                            <div>{order.user?.mobileNumber || "N/A"}</div>
                            <div className="text-[11px] text-gray-400 truncate max-w-[150px]">
                              {order.user?.emailId || "N/A"}
                            </div>
                          </td>

                          {/* Ordered On */}
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                            {formatDate(order.creationDate || order.orderDate)}
                          </td>

                          {/* Return Date */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {order.deliveryStatus === "RETURNED" ? (
                              <span className="text-teal-700 dark:text-teal-400 font-medium">
                                Returned: {formatDate(order.returnDate)}
                              </span>
                            ) : order.returnDate ? (
                              <span
                                className={
                                  order.isOverdue
                                    ? "text-red-600 font-semibold"
                                    : "text-gray-700 dark:text-gray-300"
                                }
                              >
                                {formatDate(order.returnDate)}
                                {order.isOverdue && " (Overdue)"}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusConf.badge}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${statusConf.dot}`}
                              />
                              {statusConf.label}
                            </span>
                          </td>

                          {/* Collected By */}
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                            {order.collectedBy || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ─── Modal Footer ────────────────────────────────────────── */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-850 flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Showing {filteredOrders.length} of {orders.length} orders
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-all shadow-sm"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
